"use client";

import {
    useQuery,
    useMutation,
    type UseMutationOptions,
    type UseQueryOptions,
    type QueryKey,
} from "@tanstack/react-query";
import type { AxiosRequestConfig } from "axios";
import { ClientFetchService } from "./clientFetchService";

export type ApiError = {
    error: string;
    name?: string;
    stack?: string;
    status: number;
    message?: string;
};

export type QueryOnlyConfig<TQueryFnData, TData> = Omit<
    UseQueryOptions<TQueryFnData, ApiError, TData>,
    "queryKey" | "queryFn" | "select"
>;

export type QueryConfig<TQueryFnData, TData = TQueryFnData> = QueryOnlyConfig<
    TQueryFnData,
    TData
> & {
    axiosConfig?: AxiosRequestConfig;
    select?: (data: TQueryFnData) => TData;
};

export type MutationConfig<TData, TVariables> = Omit<
    UseMutationOptions<TData, ApiError, TVariables>,
    "mutationFn"
> & {
    axiosConfig?: AxiosRequestConfig;
    method?: "POST" | "PUT" | "DELETE";
};

export const useApi = {
    useAsync: <TQueryFnData, TData = TQueryFnData>(
        queryKey: QueryKey,
        queryFunction: UseQueryOptions<TQueryFnData, ApiError, TData>["queryFn"],
        config: QueryConfig<TQueryFnData, TData> = {}
    ) =>
        useQuery<TQueryFnData, ApiError, TData>({
            queryKey,
            queryFn: queryFunction,
            ...config,
        }),

    useMutate: <TRequest, TResponse>(url: string, config?: MutationConfig<TResponse, TRequest>) => {
        const { axiosConfig, method = "POST", ...mutationConfig } = config || {};

        return useMutation<TResponse, ApiError, TRequest>({
            mutationFn: async (variables) => {
                switch (method) {
                    case "PUT":
                        return ClientFetchService.put<TResponse>(url, variables, axiosConfig);
                    case "DELETE":
                        return ClientFetchService.delete<TResponse>(url, variables, axiosConfig);
                    case "POST":
                    default:
                        return ClientFetchService.post<TRequest, TResponse>(
                            url,
                            variables,
                            axiosConfig
                        );
                }
            },
            ...mutationConfig,
        });
    },
};
